import type { FieldDefinition, ValidationIssue } from '@inside-us-aos/domain';

import styles from './MultiSelectInput.module.css';
import type { ISelectOption } from './SelectInput';
import { issueMessage } from './issueMessage';

export interface MultiSelectInputProps {
  field: FieldDefinition;
  /**
   * Caller-supplied option list — see `SelectInput.tsx` for rationale.
   */
  options: readonly ISelectOption[];
  value: readonly string[];
  issues: readonly ValidationIssue[];
  onChange: (next: string[]) => void;
  /**
   * Whether the user has interacted with the field. When `false`,
   * issues are suppressed (no red border, no error region, no
   * `aria-invalid`). Defaults to `true` for backward compatibility.
   */
  touched?: boolean;
  /** Fires when focus leaves the fieldset. */
  onBlur?: () => void;
}

/**
 * Catalog-driven multi-select primitive. Renders a fieldset with a
 * legend (the field's label) and a checkbox per option. Toggling a
 * checkbox emits the full updated array.
 *
 * USCIS multi-select fields (e.g., `beneficiary.biographic.race`) are
 * rendered as checkbox groups rather than `<select multiple>` because
 * checkboxes are more accessible (keyboard tab through each option,
 * screen reader announces checked state) and more recognisable on
 * mobile.
 */
export function MultiSelectInput({
  field,
  options,
  value,
  issues,
  onChange,
  touched = true,
  onBlur,
}: MultiSelectInputProps): JSX.Element {
  const groupId = `field-${field.id.replace(/\./g, '-')}`;
  const errorListId = `${groupId}-errors`;
  const showIssues = touched && issues.length > 0;
  const listClassName = showIssues
    ? `${styles['field__option-list']} ${styles['field__option-list--invalid']}`
    : styles['field__option-list'];
  const selectedSet = new Set(value);

  const toggle = (optionValue: string): void => {
    const next = selectedSet.has(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue];
    onChange(next);
  };

  return (
    <fieldset
      className={styles.field}
      data-field-id={field.id}
      onBlur={onBlur}
    >
      <legend className={styles.field__legend}>
        {field.label}
        {field.required === true ? (
          <span className={styles['field__required-mark']}>*</span>
        ) : null}
      </legend>

      {/* `role="group"` is the correct ARIA role for a labelled set
          of checkboxes; it doesn't support aria-required / aria-invalid /
          aria-describedby on its own (those go on individual inputs).
          The fieldset+legend already gives the group its accessible
          name; the required-asterisk in the legend communicates
          required state to AT. axe: aria-allowed-attr. */}
      <div
        className={listClassName}
        role="group"
        aria-labelledby={groupId}
      >
        {options.map(option => {
          const optionId = `${groupId}-${option.value}`;
          return (
            <div key={option.value} className={styles.field__option}>
              <input
                id={optionId}
                type="checkbox"
                checked={selectedSet.has(option.value)}
                onChange={() => toggle(option.value)}
                className={styles['field__option-input']}
              />
              <label htmlFor={optionId}>{option.label}</label>
            </div>
          );
        })}
      </div>

      {field.helpText !== undefined && field.helpText !== '' ? (
        <p className={styles['field__help-text']}>{field.helpText}</p>
      ) : null}

      {showIssues ? (
        <div role="alert">
          <ul id={errorListId} className={styles['field__error-list']}>
            {issues.map((issue, index) => (
              <li key={`${issue.code}-${index}`}>
                <span
                  aria-hidden="true"
                  className={styles['field__error-icon']}
                >
                  !
                </span>
                {issueMessage(issue)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </fieldset>
  );
}
